import type { CrmSuppressionProvider, CrmSuppressionReason, CrmSuppressionSourceKind } from '@/types/crmSuppressionSources'

/**
 * The reason choices, each stating what it will DO rather than naming itself.
 *
 * This is the part of the form that matters. The reason decides whether removing someone from the CRM
 * gives them their marketing back, and a client who reads it the wrong way round makes a decision they
 * cannot reverse themselves — so the consequence is the label's job, not a footnote.
 */
export const CRM_SUPPRESSION_REASONS: { value: CrmSuppressionReason; label: string; help: string }[] = [
  {
    value: 'Unsubscribed',
    label: 'Unsubscribed',
    help: 'They opted out of email. Removing them from the source will NOT un-suppress them.',
  },
  {
    value: 'Do Not Contact',
    label: 'Do not contact',
    help: 'Flagged do-not-contact in your CRM. Clearing the flag there clears it here.',
  },
  {
    value: 'Actively Marketing',
    label: 'You are marketing to them',
    help: 'Not an opt-out. Excluded from our marketing sends, but they still get confirmations and reminders if they register.',
  },
  {
    value: 'Compliance',
    label: 'Compliance',
    help: 'A legal or regulatory suppression. Permanent — only an OrbitalX admin can clear it.',
  },
]

export const HUBSPOT_KINDS: CrmSuppressionSourceKind[] = ['HubSpot List']

export const SALESFORCE_KINDS: CrmSuppressionSourceKind[] = [
  'Salesforce Standard Opt Out',
  'Salesforce Campaign',
  'Salesforce Field Condition',
]

/** What to call the picker for a given kind. */
export function pickerLabel(kind: CrmSuppressionSourceKind): string {
  if (kind === 'HubSpot List') return 'List'
  if (kind === 'Salesforce Campaign') return 'Campaign'
  return 'Field'
}

/** What the picker holds, for copy that has to name it in the plural. */
export function pickerNoun(kind: CrmSuppressionSourceKind): string {
  if (kind === 'HubSpot List') return 'lists'
  if (kind === 'Salesforce Campaign') return 'campaigns'
  return 'fields'
}

/**
 * What to say when the CRM answered and has nothing to offer.
 *
 * Names the thing to go and create, because the fix is in the CRM and not here. This case used to
 * advise a reconnect, which is the one action that cannot change the answer.
 */
export function emptyPickerMessage(kind: CrmSuppressionSourceKind, provider: CrmSuppressionProvider): string {
  if (kind === 'HubSpot List') return `No lists in the connected ${provider} portal. Create one in ${provider}, then reopen this.`
  if (kind === 'Salesforce Campaign') return `No campaigns in the connected ${provider} org. Create one in ${provider}, then reopen this.`
  return `No checkbox or picklist fields are available on both Contact and Lead in the connected ${provider} org.`
}
